/**
 * Citation nodes — the ProseMirror equivalent of a Word "yaqat-cite-tag"
 * content control wrapping inserted, formatted citation text (a quoted
 * highlight excerpt, a note, or an artifact interpretation — see
 * service/writer/cite.ts on the yaqatw side, which builds `formattedText`
 * before calling insertCitationAtCursor).
 *
 * Two node types rather than one dynamic-group node (ProseMirror node groups
 * are static per type) — mirrors the existing dual ImageBlock/ImageInline
 * convention in imageNode.ts:
 *  - `citationInline` sits inline within a paragraph ("inline" style).
 *  - `citationBlock` is its own block, styled like a blockquote — a
 *    deliberate simplification of Word's "apply the Quote paragraph style to
 *    whatever paragraph the cursor was in" behavior (which may have other
 *    content in it); qda instead gives block citations their own node.
 *
 * `controlId` is the identity WordDocumentHost.ts's `control.id` would be —
 * resolved by controlRegistry.ts, not stored anywhere else. `title` mirrors
 * Word's `control.title`/`w:alias`: write-only decoration (see cite.ts —
 * nothing ever reads a citation's title back), rendered here as an HTML
 * `title` tooltip for discoverability.
 */

import { Node, mergeAttributes } from "@tiptap/core";

const citationAttributes = {
  controlId: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-control-id"),
    renderHTML: (attrs: Record<string, any>) => (attrs.controlId ? { "data-control-id": attrs.controlId } : {}),
  },
  title: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("title"),
    renderHTML: (attrs: Record<string, any>) => (attrs.title ? { title: attrs.title } : {}),
  },
};

export const CitationInline = Node.create({
  name: "citationInline",
  group: "inline",
  inline: true,
  // "inline*" (not "text*") deliberately anticipates cite.ts's own documented
  // rich-text TODO (formatText's format:"rich" branch) at ~zero cost now.
  content: "inline*",
  addAttributes: () => citationAttributes,
  parseHTML: () => [{ tag: "span[data-yaqat-citation]" }],
  renderHTML: ({ HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, { "data-yaqat-citation": "", class: "qda-citation-inline" }),
    0,
  ],
});

export const CitationBlock = Node.create({
  name: "citationBlock",
  group: "block",
  content: "inline*",
  addAttributes: () => citationAttributes,
  parseHTML: () => [{ tag: 'div[data-yaqat-citation][data-yaqat-citation-style="block"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, {
      "data-yaqat-citation": "",
      "data-yaqat-citation-style": "block",
      class: "qda-citation-block",
    }),
    0,
  ],
});
